import React, { FunctionComponent, memo } from 'react'
import dayjs from 'dayjs'
import isBetween from 'dayjs/plugin/isBetween'
import FloatingText from '@sport1/react-elements/FloatingText'
import Grid from '@sport1/react-elements/Grid'
import PlatformHelper from '@sport1/react-elements/PlatformHelper'
import Touchable from '@sport1/react-elements/Touchable'
import { FontelloIcon } from '@sport1/react-elements'
import { TestID } from '../../utils/PropTypesDefinitions'
import Indicator from '../../BaseComponents/ProgressBar/ProgressIndicator'
import SmallBodyFont from '../../Theme/Fonts/SmallBody'
import CaptionFont from '../../Theme/Fonts/Caption'
import { Broadcast, BroadcastCallback } from '../types'
import { formatServerTimeToDayjs } from '../utils/dayjsHelper'
import LinkProps from '../../Link/LinkProps'

export type BroadcastCardProps = TestID &
    Broadcast &
    LinkProps & {
        /** specific width of the component */
        width?: number
        /** id of the channel the broadcast is on */
        channelId: number
        /** name of the channel the broadcast is on */
        channelName: string
        /** streaming url of the channel the broadcast is on */
        streamingUrl?: string
        /** callback for pressing the component */
        onPress?: (channel: BroadcastCallback) => void
        /** href string to navigate to the provided url (web only) */
        href?: string
        updateTime?: number
        /** called when alert icon is pressed */
        onSubscribe?: () => void
        /** affects the look of the alert icon */
        subscriptionActive?: boolean
    }

// extracts the label to display the starting and the ending time of the show
const getTimeString = (startTime: string, endTime: string): string => {
    return `${formatServerTimeToDayjs(startTime).format('HH:mm')} - ${formatServerTimeToDayjs(
        endTime
    ).format('HH:mm')}`
}

// fill the progress bar based on the current time and comparing it with the starting and ending time of the show
const getPercentage = (startTime: string, endTime: string): number => {
    // uses the fake time provided by the story only if it's defined, otherwise uses the current time
    const now = dayjs()
    const start = formatServerTimeToDayjs(startTime)
    const end = formatServerTimeToDayjs(endTime)
    // the show is finished
    if (now.isAfter(end)) return 100
    // calculates the time already passed (in percentage) from the start of the show till now
    return Math.floor((now.diff(start) * 100) / end.diff(start))
}

const BroadcastCard: FunctionComponent<BroadcastCardProps> = ({
    programTitle,
    episodeTitle,
    startTime,
    endTime,
    emissionType,
    width,
    onPress,
    href,
    channelId,
    channelName,
    streamingUrl,
    testID,
    updateTime = 120000,
    onSubscribe,
    subscriptionActive,
    webRel,
}) => {
    dayjs.extend(isBetween)
    const now = dayjs()
    const isSmall = width ? width < 100 : false

    const [percentage, setPercentage] = React.useState(
        now.isAfter(formatServerTimeToDayjs(startTime))
            ? getPercentage(startTime, endTime)
            : undefined
    )

    const isCurrentyLive = percentage && percentage < 100

    React.useEffect(() => {
        const interval = setInterval(() => {
            if (now.isAfter(formatServerTimeToDayjs(startTime))) {
                if (isCurrentyLive) {
                    setPercentage(getPercentage(startTime, endTime))
                } else {
                    clearInterval(interval)
                }
            } else {
                setPercentage(undefined)
            }
        }, updateTime)
        return () => {
            clearInterval(interval)
        }
    }, [now, startTime, endTime, isCurrentyLive, updateTime])

    const stickyProps =
        !PlatformHelper.isNative &&
        (isCurrentyLive ||
            formatServerTimeToDayjs(endTime).isBetween(now, dayjs().subtract(1, 'hour')))
            ? { position: 'sticky', left: 0 }
            : {}

    const broadcastComponent = (
        <Grid
            noExpand
            testID={testID}
            backgroundColor="pearl"
            height={88}
            paddingTop={5}
            width={width}
            borderRight={1}
            borderTop={1}
            borderColor="tahitian-4"
        >
            <Grid noExpand horizontal width={width} flexGrow={1}>
                <Grid paddingX={isSmall ? 1 : 5} minWidth={0} {...stickyProps}>
                    <Grid noExpand marginBottom={episodeTitle ? 1 : 20}>
                        <SmallBodyFont numberOfLines={1} bold>
                            {programTitle}
                        </SmallBodyFont>
                    </Grid>
                    {episodeTitle || onSubscribe ? (
                        <Grid horizontal centerVertical noExpand>
                            {episodeTitle ? (
                                <Grid noExpand marginBottom={1}>
                                    <CaptionFont color="keshi-3" numberOfLines={1}>
                                        {episodeTitle}
                                    </CaptionFont>
                                </Grid>
                            ) : null}
                            <Grid flexGrow={1} />
                            {onSubscribe ? (
                                <Touchable onPress={onSubscribe}>
                                    <FontelloIcon
                                        name={subscriptionActive ? 'alert-active' : 'alert'}
                                        fontSize="14px"
                                        color={subscriptionActive ? 'ORANGE' : 'keshi-3'}
                                        paddingLeft={3}
                                    />
                                </Touchable>
                            ) : null}
                        </Grid>
                    ) : null}
                    <Grid noExpand marginBottom={1}>
                        <FloatingText
                            numberOfLines={1}
                            textObjects={[
                                {
                                    content: getTimeString(startTime, endTime),
                                    textStyle: {
                                        fontSize: 15,
                                        fontFamily: 'Sport1-Regular',
                                        color: 'onyx',
                                    },
                                },
                                ...(emissionType
                                    ? [
                                          {
                                              content: ` ${emissionType.toUpperCase()}`,
                                              textStyle: {
                                                  fontSize: 15,
                                                  fontFamily: 'Sport1-Regular',
                                                  color: 'ORANGE',
                                              },
                                          },
                                      ]
                                    : []),
                            ]}
                        />
                    </Grid>
                </Grid>
            </Grid>
            {isCurrentyLive && percentage ? (
                <Grid noExpand paddingX={isSmall ? 1 : 5}>
                    <Indicator indicatorPercent={percentage} />
                </Grid>
            ) : null}
        </Grid>
    )

    const onBroadcastPress = onPress
        ? (): void => {
              onPress({ channelId, channelName, streamingUrl })
          }
        : undefined

    return isCurrentyLive && (onBroadcastPress || href) ? (
        <Touchable onPress={onBroadcastPress} href={href} testID="live-broadcast" webRel={webRel}>
            {broadcastComponent}
        </Touchable>
    ) : (
        broadcastComponent
    )
}

export default memo(BroadcastCard)
